/**
 * Spreading — consolidates four legacy SFTS pages into one report: Spreading By Part
 * (`sftsRpt/spreading/spreadingByPartReport`), Spreading Detail, Spreading Summary and Spreading
 * Detail By Length (`sftsRpt/spreading/spreadingDetailByLength`).
 *
 * This MR pair adds `detail` and `summary` to the `by-part` view already here. The three share one
 * filter bar and one report id — a view is not a report, so adding one is not a new port (§5.1) —
 * and `detail` and `summary` share one authored SQL block, so a summary row and the rows a reader
 * drills into cannot disagree about which bundles were spread.
 *
 * Two legacy load gates are kept, both cited to the JavaScript they come from and neither inventing
 * a threshold: Summary refuses a window over 90 days unless an MO or a style narrows it
 * (`spreadingSummaryReport.js` ~80-91), and By length keeps its 30-day query cap. A refused query is
 * never sent — the page says what to do instead.
 *
 * Legacy's third gate is deliberately NOT kept: Detail refused to run without a spreading table,
 * bed, style, batch or size (`spreadingDetailReport.js` ~148-159). Here factory and a date range are
 * both required, so the query is already bounded — the gate was compensating for a scope this port
 * enforces up front (owner, 2026-08-12; improvements.md).
 *
 * `batch`, `size` and `part` are not query filters any more; they are in-table column filters over
 * a complete result (§4.3), which is also where the Summary drill-through puts them.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ExportMenu } from '@/components/report/ExportMenu';
import { exportColumns, groupedColumns, integer } from '@/components/report/groupedColumns';
import {
  headerFilterOptions,
  headerFiltersAreOffered,
  matchesHeaderFilters,
  withHeaderFilters,
  type HeaderFilterSelection,
} from '@/components/report/headerFilter';
import { useReportFilters, useReportSort } from '@/components/report/useReportFilters';
import { Button } from '@/components/ui/button';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportQueryRequest, SortSpec } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { resolveFactory } from '@/lib/factories';
import { cn } from '@/lib/utils';

import {
  EXPORT_RANGE_DAYS,
  QUERY_RANGE_DAYS,
  onlySummableTotals,
  rangeDays,
  totalsFormat,
} from './byLength';
import { ByPartTable, type ByPartRow } from './byPart';
import { hasLegacyParams, translateDeepLink } from './deepLink';
import { spreadingFilters } from './filters';

type Tab = 'summary' | 'by-size' | 'detail' | 'by-length' | 'by-part';

const REPORT_ID = 'spreading';

// Ordered as the cutting room reads them: the roll-up first, then the bundles behind it, then the
// markers, then the order-coverage view. Not the order they were ported in.
const TABS: ReadonlyArray<readonly [Tab, string]> = [
  ['summary', 'Summary'],
  ['by-size', 'By Size'],
  ['detail', 'By Part-Size'],
  ['by-length', 'By Marker'],
  ['by-part', 'Pivot'],
];

/**
 * No default sort for by-marker. Its grain is the spread and marker ids, which the query still
 * orders on but no longer returns — internal keys are not a reader's business — so they cannot be
 * named here either. Absent a request, the file's own ORDER BY applies, which is that same grain.
 */
const DEFAULT_BY_LENGTH_SORT: SortSpec[] = [];

/** Legacy's own cap, `3*30*24*60*60*1000` ms in `spreadingSummaryReport.js`. Not a new threshold. */
const SUMMARY_MAX_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

/** In-table column filters, per tab — the capability `batch`, `size` and `part` moved into. */
const HEADER_FILTER_FIELDS: Record<Tab, readonly string[]> = {
  'by-part': [],
  // `bed_no` is here rather than in the filter bar for the same reason it is not a query filter:
  // a lay sequence number is not a resource identity. It is one of the six keys the Summary
  // drill-through carries, so it has to narrow something.
  detail: ['batch', 'size', 'part', 'bed_no'],
  summary: ['batch'],
  'by-size': ['batch', 'size'],
  // By length arrives in its own grain order and carries no dropped filter to re-home; a reader's
  // own ordering is applied in the browser (§4.3, DECISIONS.md §30), not asked of the query.
  'by-length': [],
};
const HEADER_FILTER_PREFIX = 'col.';
const NO_HEADER_FILTERS: HeaderFilterSelection = {};

/**
 * Measures that repeat an order-level figure across the rows of an order. Summing them counts the
 * order once per size, so they are taken back out of the totals row the shared column builder
 * offers every numeric column.
 */
const NOT_ADDITIVE = new Set(['order_qty']);

function isTab(value: string | null): value is Tab {
  return (
    value === 'summary' || value === 'by-size' || value === 'detail'
    || value === 'by-length' || value === 'by-part'
  );
}

/** Whole days between two ISO dates, inclusive of both ends — legacy counted the same span. */
function windowDays(from: string | null, to: string | null): number | null {
  if (from === null || to === null || from === '' || to === '') return null;
  const span = Date.parse(to) - Date.parse(from);
  return Number.isNaN(span) ? null : span / DAY_MS + 1;
}

/** Drop the totals flag from a column the report has declared non-additive. */
function withoutFalseTotals<TRow>(columns: ColumnDef<TRow>[]): ColumnDef<TRow>[] {
  return columns.map((column) => {
    if (column.id === undefined || !NOT_ADDITIVE.has(column.id)) return column;
    const meta: Record<string, unknown> = { ...column.meta };
    delete meta.total;
    return { ...column, meta } as ColumnDef<TRow>;
  });
}

export default function SpreadingReport() {
  const { user, status } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const factory = resolveFactory(searchParams.get('factory'), user?.defaultFactory);
  const schema = useMemo(() => spreadingFilters(factory), [factory]);
  const { values, isComplete, setValue } = useReportFilters(schema);

  const requestedTab = searchParams.get('view');
  const tab: Tab = isTab(requestedTab) ? requestedTab : 'summary';

  // Legacy dashboard links land here with the old parameter names. Translated once, replacing the
  // entry so Back does not bounce through the untranslated address — see deepLink.ts.
  const legacyLink = useMemo(
    () => (hasLegacyParams(searchParams) ? translateDeepLink(searchParams) : null),
    [searchParams],
  );
  // The note outlives the translation: the legacy parameters are gone from the URL a render later,
  // and what could not be honoured has to stay on screen. Read once, from the address the reader
  // arrived on — presentation state, so not URL state.
  const [deepLinkNotice] = useState<string | null>(() => legacyLink?.unsupported ?? null);
  useEffect(() => {
    if (legacyLink === null) return;
    setSearchParams(legacyLink.params, { replace: true });
  }, [legacyLink, setSearchParams]);

  // Write the factory into the URL, or a shared link resolves to the *recipient's* factory — the
  // same address showing two people different numbers.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (searchParams.get('factory') === factory) return;
    setValue('factory', factory);
  }, [status, searchParams, factory, setValue]);

  const setUrlParam = useCallback(
    (name: string, value: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null) next.delete(name);
          else next.set(name, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const baseFilters = useMemo<ReportQueryRequest['filters']>(
    () => ({
      from: values.from ?? null,
      to: values.to ?? null,
      factory: values.factory ?? null,
      spreading_table: values.spreading_table ?? null,
      b_no: values.b_no ?? null,
      mo: values.mo ?? null,
      working_no: values.working_no ?? null,
      building: values.building ?? null,
    }),
    [values],
  );

  // Only `detail` and `summary` declare `is_main_body` (`views:` in report.yaml). Sending it to a
  // view that does not is a 400, not an ignored key, so the two sets are kept apart rather than
  // one set being sent everywhere.
  const viewFilters = useMemo<ReportQueryRequest['filters']>(
    () => ({ ...baseFilters, is_main_body: values.is_main_body ?? null }),
    [baseFilters, values],
  );

  const hasOrder = (values.mo ?? '') !== '' || (values.b_no ?? '') !== '';
  const days = windowDays(values.from ?? null, values.to ?? null);

  /**
   * Legacy Summary refused a window over 90 days unless an MO or a style narrowed it
   * (`spreadingSummaryReport.js` ~80-91). Reproduced as a gate, so the query is never sent.
   */
  const summaryBlocked =
    !hasOrder && days !== null && days > SUMMARY_MAX_DAYS
      ? `A summary over more than ${String(SUMMARY_MAX_DAYS)} days is not run. `
        + 'Shorten the spreading date range, or name an MO or a B No.'
      : null;

  const byPart = useReportQuery<ByPartRow>(REPORT_ID, baseFilters, {
    view: 'by-part',
    enabled: isComplete && tab === 'by-part',
  });
  const detail = useReportQuery<Record<string, unknown>>(REPORT_ID, viewFilters, {
    view: 'detail',
    enabled: isComplete && tab === 'detail',
  });
  const summary = useReportQuery<Record<string, unknown>>(REPORT_ID, viewFilters, {
    view: 'summary',
    enabled: isComplete && tab === 'summary' && summaryBlocked === null,
  });
  const bySize = useReportQuery<Record<string, unknown>>(REPORT_ID, viewFilters, {
    view: 'by-size',
    enabled: isComplete && tab === 'by-size' && summaryBlocked === null,
  });

  /**
   * Legacy By Length capped its query at 30 days and its export at 90
   * (`spreadingDetailByLength.js` ~45-48, ~106-109). Over the query cap the request is never sent:
   * a range refused after two minutes is worse than one refused at once. The export cap is wider
   * because the file is produced server-side — a period too big to draw is still one to take away.
   */
  const byLengthDays = rangeDays(values.from, values.to);
  const overQueryCap = byLengthDays !== null && byLengthDays > QUERY_RANGE_DAYS;
  const overExportCap = byLengthDays !== null && byLengthDays > EXPORT_RANGE_DAYS;
  const byLengthBlocked =
    tab === 'by-length' && overQueryCap
      ? `By length covers up to ${String(QUERY_RANGE_DAYS)} days at a time, and this range is `
        + `${String(Math.round(byLengthDays))}. Shorten the spreading date range.`
      : null;

  const { sort, setSort } = useReportSort(DEFAULT_BY_LENGTH_SORT);
  const byLength = useReportQuery<Record<string, unknown>>(REPORT_ID, baseFilters, {
    view: 'by-length',
    enabled: isComplete && tab === 'by-length' && !overQueryCap,
  });

  const active =
    tab === 'by-part' ? byPart
    : tab === 'by-size' ? bySize
    : tab === 'detail' ? detail
    : tab === 'summary' ? summary
    : byLength;
  const blocked =
    tab === 'summary' || tab === 'by-size' ? summaryBlocked
    : tab === 'by-length' ? byLengthBlocked
    : null;
  const rows = useMemo(
    () => (active.data?.rows ?? []) as Record<string, unknown>[],
    [active.data],
  );

  const headerFields = HEADER_FILTER_FIELDS[tab];
  const headerFilters = useMemo<HeaderFilterSelection>(() => {
    const selection: Record<string, string[]> = {};
    for (const field of headerFields) {
      selection[field] = searchParams.getAll(`${HEADER_FILTER_PREFIX}${field}`);
    }
    return selection;
  }, [searchParams, headerFields]);

  // Only over a complete result: filtering a capped one hides rows the reader cannot know about.
  const offerHeaderFilters = headerFields.length > 0 && headerFiltersAreOffered(active.data);
  const activeHeaderFilters = offerHeaderFilters ? headerFilters : NO_HEADER_FILTERS;

  const headerFilterOptionsByField = useMemo(
    () => headerFilterOptions(rows, headerFields),
    [rows, headerFields],
  );

  const onHeaderFilterChange = useCallback(
    (field: string, next: string[]) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          const name = `${HEADER_FILTER_PREFIX}${field}`;
          params.delete(name);
          for (const value of next) params.append(name, value);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const tableRows = useMemo(
    () =>
      offerHeaderFilters
        ? rows.filter((row) => matchesHeaderFilters(row, headerFields, activeHeaderFilters))
        : rows,
    [rows, offerHeaderFilters, headerFields, activeHeaderFilters],
  );

  /**
   * Summary → Detail, as legacy's per-row "view" link did — one in-page view switch, not a route
   * change, matching on exactly the six keys legacy matched on
   * (`spreadingSummaryReport.js` line 21). `size` and `batch` are no longer query filters, so they
   * travel as in-table column filter state and narrow the detail result to the same cell.
   */
  const openInDetail = useCallback(
    (row: Record<string, unknown>) => {
      const text = (field: string) => {
        const value = row[field];
        return value === null || value === undefined ? '' : String(value);
      };
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('view', 'detail');
          next.set('factory', text('factory'));
          next.set('spreading_table', text('spreading_table'));
          next.set('b_no', text('b_no'));
          for (const field of ['batch', 'size']) {
            const name = `${HEADER_FILTER_PREFIX}${field}`;
            next.delete(name);
            const value = text(field);
            if (value !== '') next.append(name, value);
          }
          // Bed no is a lay sequence number rather than a resource identity, so it is not a
          // filter — it narrows the detail rows as a column filter alongside batch and size.
          const bed = `${HEADER_FILTER_PREFIX}bed_no`;
          next.delete(bed);
          if (text('bed_no') !== '') next.append(bed, text('bed_no'));
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const columns = useMemo(() => {
    if (tab === 'by-part') return [];
    const grouped = groupedColumns<Record<string, unknown>>(active.data?.columns ?? []);
    // By length's numbers are mostly marker attributes rather than quantities, so it strips the
    // default totals flag by its own rule; the bundle views only have to drop `order_qty`.
    const built =
      tab === 'by-length' ? onlySummableTotals(grouped) : withoutFalseTotals(grouped);
    const filtered = withHeaderFilters(
      built,
      headerFields,
      activeHeaderFilters,
      headerFilterOptionsByField,
      onHeaderFilterChange,
    );
    if (tab !== 'summary' && tab !== 'by-size') return filtered;
    const drill: ColumnDef<Record<string, unknown>> = {
      id: 'open',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          type="button"
          variant="link"
          size="xs"
          // Underlined at rest, not only on hover: it is the one cell on the row that does
          // something, and a link that only looks like one under the pointer is not discoverable.
          className="px-1 underline"
          // Both detail tabs begin with the word "Detail", so the visible label names the grain
          // this one lands on. The full destination goes to assistive tech, where there is room.
          aria-label="Open in By Part-Size"
          onClick={() => {
            openInDetail(row.original);
          }}
        >
          Part-size
        </Button>
      ),
    };
    return [drill, ...filtered];
  }, [
    tab,
    active.data,
    headerFields,
    activeHeaderFilters,
    headerFilterOptionsByField,
    onHeaderFilterChange,
    openInDetail,
  ]);

  // Only over a complete result (§4.3): a total over a capped one presents part as the whole.
  const totals =
    active.data !== undefined && !active.data.truncated && tab !== 'by-part'
      ? {
          labelColumn:
            tab === 'summary' ? 'factory'
            : tab === 'by-length' ? 'start_spreading_time'
            : 'spreading_time',
          label: 'Total',
          format: tab === 'by-length' ? totalsFormat : (value: number) => integer.format(Math.round(value)),
        }
      : undefined;

  // The export must carry the same filter set its tab queried with, or it 400s on the views that
  // do not declare `is_main_body` exactly as the query would.
  const exportTarget =
    tab === 'by-part'
      ? { request: { view: 'by-part', filters: baseFilters }, columns: undefined }
      : {
          request: {
            view: tab,
            filters: tab === 'by-length' ? baseFilters : viewFilters,
          },
          columns: exportColumns(columns),
        };

  const viewTabs = (
    <div role="tablist" aria-label="View" className="flex gap-0.5 border-b border-border">
      {TABS.map(([id, label]) => (
        <Button
          key={id}
          role="tab"
          variant="ghost"
          aria-selected={tab === id}
          onClick={() => {
            setUrlParam('view', id);
          }}
          className={cn(
            'h-8 rounded-none border-b-2 border-transparent px-3 text-sm',
            tab === id && 'border-primary font-medium text-foreground',
          )}
        >
          {label}
        </Button>
      ))}
    </div>
  );

  const table = !isComplete ? (
    <DataTable
      columns={[]}
      data={[]}
      emptyMessage="Choose a spreading date range and a factory to run the report."
    />
  ) : blocked !== null ? (
    <DataTable columns={[]} data={[]} emptyMessage={blocked} />
  ) : tab === 'by-part' ? (
    <ByPartTable rows={byPart.data?.rows ?? []} isLoading={byPart.isLoading} />
  ) : (
    <DataTable
      columns={columns}
      data={tableRows}
      isLoading={active.isLoading}
      virtualized
      totals={totals}
      truncated={active.data?.truncated === true}
      {...(tab === 'by-length' ? { sort, onSortChange: setSort } : {})}
      caption={
        tab === 'detail'
          ? 'Every spread laid in the period, by batch, set group and size'
          : tab === 'by-length'
            ? 'Every marker spread in the period, with its length, layers and fabric'
            : 'Spread quantity by work group, bed, style, batch, set group and size'
      }
      emptyMessage="No spreading recorded for these filters. Widen the date range, or clear the column filters."
    />
  );

  return (
    <ReportPage
      title="Spreading"
      description={
        deepLinkNotice
        ?? 'What was spread in the period — by part, in detail, summarised, or marker by marker.'
      }
      filters={
        <FilterBar
          schema={schema}
          actions={
            <ExportMenu
              reportId={REPORT_ID}
              request={exportTarget.request}
              columns={exportTarget.columns}
              disabled={
                !isComplete || blocked !== null || (tab === 'by-length' && overExportCap)
              }
            />
          }
        />
      }
      views={viewTabs}
      table={table}
      isLoading={active.isLoading}
      error={active.error}
      generatedAt={active.data?.generatedAt ?? null}
      dataAsOf={active.data?.dataAsOf ?? null}
      truncated={active.data?.truncated ?? false}
    />
  );
}
